import React, { useEffect, useState } from "react";
import { Navigate, Link, useNavigate } from "react-router-dom";
import api from "@/api/client";
import { useAuth } from "@/context/AuthContext";
import { Shield, Users, FileText, Trash2, Star, ArrowLeft, Loader2 } from "lucide-react";

export default function AdminDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [articles, setArticles] = useState([]);
  const [tab, setTab] = useState("users");
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(""), 3000); };

  useEffect(() => {
    (async () => {
      try {
        const [s, u, a] = await Promise.all([
          api.get("/admin/stats"),
          api.get("/admin/users"),
          api.get("/admin/articles?limit=100"),
        ]);
        setStats(s.data); setUsers(u.data); setArticles(a.data);
      } catch (e) { /* auth handled by route guard */ }
      finally { setLoading(false); }
    })();
  }, []);

  const toggleEditorsPick = async (article) => {
    try {
      await api.patch(`/public/articles/${article.id}/editors-pick`, {
        is_editors_pick: !article.is_editors_pick,
        order: article.editors_pick_order || 0,
      });
      showToast(article.is_editors_pick ? "Removed from picks." : "Added to Editor's Picks.");
      setArticles((prev) => prev.map((a) => a.id === article.id ? { ...a, is_editors_pick: !a.is_editors_pick } : a));
    } catch (e) {
      showToast(e.response?.data?.detail || "Failed");
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article? This cannot be undone.")) return;
    try {
      await api.delete(`/admin/articles/${id}`);
      setArticles((prev) => prev.filter((a) => a.id !== id));
      showToast("Article deleted.");
    } catch (e) {
      showToast("Delete failed");
    }
  };

  // Route guard — allow both admin and owner
  if (user && !["admin", "owner"].includes(user.role)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      {toast && (
        <div data-testid="admin-toast" className="fixed bottom-6 right-6 z-50 bg-sky-400 text-slate-950 px-5 py-3 rounded-md font-medium shadow-2xl">{toast}</div>
      )}
      <header className="border-b border-slate-800 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/app/dashboard")} className="text-slate-500 hover:text-sky-300 flex items-center gap-1 text-xs font-mono uppercase tracking-widest">
            <ArrowLeft className="w-3 h-3" /> Back to app
          </button>
          <div className="border-l border-slate-800 pl-4">
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-sky-400" />
              <h1 className="font-serif text-xl text-sky-100">Admin Dashboard</h1>
              <span className="text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border border-sky-400/40 text-sky-300 bg-sky-500/5">
                {user?.role}
              </span>
            </div>
          </div>
        </div>
        {user?.role === "owner" && (
          <Link data-testid="link-to-owner" to="/owner" className="text-xs px-3 py-1.5 rounded border border-sky-400 text-sky-200 hover:bg-sky-400 hover:text-slate-950 transition">
            Owner console →
          </Link>
        )}
      </header>

      <div className="max-w-[1400px] mx-auto px-6 py-8 fade-up">
        {loading ? (
          <div className="text-center py-16 text-slate-500 flex items-center justify-center gap-3">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading admin console…
          </div>
        ) : (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Users", value: stats?.total_users, icon: Users },
                { label: "Articles", value: stats?.total_articles, icon: FileText },
                { label: "Public", value: stats?.public_articles, icon: Star },
                { label: "Content total", value: stats?.total_content_generated, icon: FileText },
              ].map((s, i) => {
                const Icon = s.icon;
                return (
                  <div key={i} data-testid={`admin-stat-${s.label.toLowerCase()}`} className="border border-slate-800 rounded-xl p-4">
                    <div className="flex items-center justify-between">
                      <p className="font-mono text-[10px] uppercase tracking-widest text-slate-500">{s.label}</p>
                      <Icon className="w-4 h-4 text-sky-400" strokeWidth={1.5} />
                    </div>
                    <p className="font-serif text-3xl font-numeric text-sky-100 mt-2">{s.value ?? "—"}</p>
                  </div>
                );
              })}
            </div>

            {/* Tabs */}
            <div className="flex gap-2 border-b border-slate-800 mb-6">
              {["users", "articles"].map((t) => (
                <button
                  key={t}
                  data-testid={`admin-tab-${t}`}
                  onClick={() => setTab(t)}
                  className={`px-4 py-2 text-sm font-mono uppercase tracking-widest transition ${
                    tab === t ? "text-sky-300 border-b-2 border-sky-400" : "text-slate-500 hover:text-sky-200"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>

            {tab === "users" && (
              <div className="border border-slate-800 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-900">
                    <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-slate-500">
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Role</th>
                      <th className="px-4 py-3">Articles</th>
                      <th className="px-4 py-3">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-t border-slate-800 hover:bg-slate-900/40">
                        <td className="px-4 py-3 font-mono text-xs text-sky-100">{u.email}</td>
                        <td className="px-4 py-3">{u.name || "—"}</td>
                        <td className="px-4 py-3">
                          <span className={`text-[10px] font-mono uppercase tracking-widest px-2 py-0.5 rounded border ${
                            u.role === "owner" ? "border-amber-400 text-amber-300"
                              : u.role === "admin" ? "border-sky-400/50 text-sky-300"
                              : "border-slate-700 text-slate-400"
                          }`}>
                            {u.role}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-numeric">{u.article_count}</td>
                        <td className="px-4 py-3 text-slate-500 text-xs font-mono">{(u.created_at || "").slice(0, 10)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {tab === "articles" && (
              <div className="space-y-2">
                {articles.length === 0 ? (
                  <p className="text-slate-500 text-center py-12">No articles yet.</p>
                ) : articles.map((a) => (
                  <div key={a.id} data-testid={`admin-article-${a.id}`} className="border border-slate-800 rounded-lg p-4 flex items-center justify-between gap-4 hover:border-sky-400/30 transition">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-mono text-[10px] uppercase tracking-widest text-sky-400 truncate">{a.niche}</p>
                        {a.is_public && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-emerald-500/40 text-emerald-300">Public</span>}
                        {a.is_editors_pick && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-300">Pick</span>}
                        {a.auto_generated && <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded border border-sky-500/40 text-sky-300">Auto</span>}
                      </div>
                      <h4 className="font-serif text-base text-sky-100 line-clamp-1">{a.title}</h4>
                      <p className="text-[11px] font-mono text-slate-500 mt-1">by {a.author_name} · {a.language || "EN"} · SEO {a.seo_score}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {a.is_public && (
                        <button
                          data-testid={`admin-pick-${a.id}`}
                          onClick={() => toggleEditorsPick(a)}
                          className={`text-xs px-3 py-1.5 rounded border transition flex items-center gap-1 ${
                            a.is_editors_pick
                              ? "border-amber-400 text-amber-300 bg-amber-500/10"
                              : "border-slate-700 text-slate-400 hover:border-amber-400/40 hover:text-amber-300"
                          }`}
                        >
                          <Star className="w-3 h-3" /> {a.is_editors_pick ? "Un-pick" : "Star"}
                        </button>
                      )}
                      <button
                        data-testid={`admin-delete-${a.id}`}
                        onClick={() => deleteArticle(a.id)}
                        className="text-xs px-3 py-1.5 rounded border border-slate-700 text-slate-400 hover:border-red-400/50 hover:text-red-300"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
